import React, { useEffect, useState } from "react";
import { Box, Typography, Switch, IconButton, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  addEditSkill,
  getSkillsSettings,
  removeSkill,
} from "../../../components/clients/clientsSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs from "../../../app/lang/langs";
import ConfirmDialog from "../../../components/popups/ConfirmDialog";
import AddSkillModal from "../Modals/AddSkillModal";

const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

export default function MobileSkillsTab({ mobileAddRef }) {
  const dispatch = useDispatch();

  const skillsSlice = useSelector((state) => state.clients.skills);

  const [skills, setSkills] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [onEditSkill, setOnEditSkill] = useState(null);
  const [onEditName, setOnEditName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    setSkills(skillsSlice);
  }, [skillsSlice]);

  useEffect(() => {
    dispatch(getSkillsSettings());
  }, []);

  useEffect(() => {
    if (mobileAddRef) {
      mobileAddRef.current = () => setAddModalVisible(true);
      return () => {
        mobileAddRef.current = null;
      };
    }
  }, [mobileAddRef]);

  const handleActivateSkill = (e, rowData) => {
    const newActive = e.target.checked ? 1 : 0;
    setSkills(
      skills.map((p) =>
        p.id === rowData.id ? { ...p, active: newActive } : p
      )
    );
    dispatch(addEditSkill({ id: rowData.id, active: newActive })).then(
      (actionResponse) => {
        const { responseData, responseError } = actionResponse.payload || {};
        if (responseData && responseData.status) {
          dispatch(getSkillsSettings());
          newActive
            ? showSuccessToast("Compétence activé")
            : showErrorToast("Compétence désactivé");
        } else if (responseData && responseData.status === 0) {
          showErrorToast(responseData.message);
        } else if (responseError) {
          showErrorToast(responseError.data?.error || responseError.statusText);
        }
      }
    );
  };

  const handleEditSkillSave = (name, id) => {
    if (name.trim() !== "" && id != null) {
      dispatch(addEditSkill({ id: id, name: name }));
      setSkills((current) =>
        current.map((skill) =>
          skill.id == id ? { ...skill, name: name } : skill
        )
      );
      setOnEditSkill(null);
      setOnEditName("");
    }
  };

  const handleDeleteSkill = (id) => {
    dispatch(removeSkill({ id: id })).then((actionResponse) => {
      const { responseData, responseError } = actionResponse.payload || {};
      if (responseData && responseData.status) {
        dispatch(getSkillsSettings());
        showSuccessToast(responseData.message);
      } else if (responseData && responseData.status === 0) {
        showErrorToast(responseData.message);
      } else if (responseError) {
        showErrorToast(responseError.data?.error || responseError.statusText);
      }
    });
  };

  const openDeleteDialog = (id) => {
    setPendingDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (pendingDeleteId != null) {
      handleDeleteSkill(pendingDeleteId);
    }
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  return (
    <Box sx={{ width: "100%", mt: 0, pb: 2, px: 0.5, pt: 1 }}>
      {skills.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "rgba(0,0,0,0.35)",
              fontWeight: 500,
            }}
          >
            <Langs str="NO_DATA" />
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {skills.map((skill, idx) => (
            <Box
              key={skill.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                bgcolor: "#fff",
                borderRadius: "12px",
                boxShadow:
                  "0 1px 3px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)",
                px: 1.5,
                py: 0.75,
                minHeight: 56,
                transition: TRANSITION_SMOOTH,
                animation: `fadeSlideIn 0.3s ease ${idx * 30}ms both`,
                "@keyframes fadeSlideIn": {
                  from: { opacity: 0, transform: "translateY(8px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
                ...(skill.active != 1 && {
                  opacity: 0.65,
                }),
              }}
            >
              {/* Active switch */}
              <Switch
                checked={skill.active == 1}
                onChange={(e) => handleActivateSkill(e, skill)}
                size="small"
                sx={{
                  flexShrink: 0,
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#FF9015",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#FF9015",
                  },
                }}
              />

              {/* Name or inline edit field */}
              {onEditSkill === skill.id ? (
                <TextField
                  value={onEditName}
                  onChange={(e) => setOnEditName(e.target.value)}
                  size="small"
                  autoFocus
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleEditSkillSave(onEditName, skill.id);
                    if (e.key === "Escape") {
                      setOnEditSkill(null);
                      setOnEditName("");
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    "& .MuiInputBase-input": {
                      fontSize: "16px",
                      py: "6px",
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#FF9015",
                      },
                    },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "0.925rem",
                    fontWeight: 500,
                    color: skill.active == 1 ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0.45)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    transition: "color 0.2s ease",
                  }}
                >
                  {skill.name}
                </Typography>
              )}

              {/* Action buttons */}
              {onEditSkill === skill.id ? (
                <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  {/* Cancel */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setOnEditSkill(null);
                      setOnEditName("");
                    }}
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: "rgba(0,0,0,0.04)",
                      borderRadius: "10px",
                      transition: TRANSITION_SMOOTH,
                      "&:active": { transform: "scale(0.93)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18, color: "#666" }} />
                  </IconButton>
                  {/* Save */}
                  {onEditName.trim() !== "" && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditSkillSave(onEditName, skill.id)}
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: "rgba(76, 175, 80, 0.10)",
                        border: "1px solid rgba(76, 175, 80, 0.25)",
                        borderRadius: "10px",
                        transition: TRANSITION_SMOOTH,
                        "&:active": { transform: "scale(0.93)" },
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  {/* Edit */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setOnEditSkill(skill.id);
                      setOnEditName(skill.name);
                    }}
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: "rgba(255, 144, 21, 0.08)",
                      border: "1px solid rgba(255, 144, 21, 0.18)",
                      borderRadius: "10px",
                      transition: TRANSITION_SMOOTH,
                      "&:active": {
                        bgcolor: "rgba(255, 144, 21, 0.2)",
                        transform: "scale(0.93)",
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18, color: "#FF9015" }} />
                  </IconButton>
                  {/* Delete */}
                  <IconButton
                    size="small"
                    onClick={() => openDeleteDialog(skill.id)}
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: "rgba(211, 47, 47, 0.06)",
                      border: "1px solid rgba(211, 47, 47, 0.15)",
                      borderRadius: "10px",
                      transition: TRANSITION_SMOOTH,
                      "&:active": {
                        bgcolor: "rgba(211, 47, 47, 0.15)",
                        transform: "scale(0.93)",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        danger
        title={<Langs str="DELETE_SKILL" />}
        message={<Langs str="DELETE_SKILL_MESSAGE" />}
        confirmLabel={<Langs str="DELETE" />}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <AddSkillModal visible={addModalVisible} setVisible={setAddModalVisible} />
    </Box>
  );
}
